/**
 * How to look at what is on the canvas.
 *
 * An app view is HTML someone wrote, and an instance is structured data, but
 * both render only as the finished thing. There is no way to see what a
 * document actually IS without leaving the app, which is a poor answer when the
 * document was written by an agent thirty seconds ago and you want to know what
 * it did. Patchwork's move: the artifact is one thing, the tool you view it
 * with is another, and switching is a menu rather than a different app.
 *
 * Four modes, not a plugin surface. "Preview" is the thing to be READ, with
 * every affordance for changing it taken away; "App" is the working view you
 * edit in; "Data" is the structured fields with document references resolved and
 * expandable; "Source" is the bytes underneath. More tools (a diff of the last
 * change, Automerge history) are the same shape when something needs them.
 *
 * PREVIEW IS FIRST because it is what a document is FOR. A plan, an RFC, a
 * review: the common act on all three is reading one, and until now reading one
 * meant reading it inside an editor, with an Edit button over the corner, a
 * comment popover on every selection and a side column taking a quarter of the
 * width. None of that is wrong while you work; all of it is noise while you
 * read, and worse than noise when the document is being shown to somebody.
 */

import { BookOpen, Braces, Code2, MoreHorizontal, Play } from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'

export type CanvasViewMode = 'preview' | 'app' | 'data' | 'source'

export function ViewModeMenu({
  mode,
  onSelect,
}: {
  mode: CanvasViewMode
  onSelect: (mode: CanvasViewMode) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close on an outside click or Escape. Both, because a menu that traps the
  // pointer is worse than one that closes too eagerly.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const items: { id: CanvasViewMode; label: string; icon: ReactElement }[] = [
    { id: 'preview', label: 'Preview', icon: <BookOpen size={13} /> },
    { id: 'app', label: 'App', icon: <Play size={13} /> },
    // Between the rendered thing and its raw bytes: the fields, with references
    // resolved. For anything that points at other documents this is the one you
    // actually want, which is why it sits directly under App.
    { id: 'data', label: 'Data', icon: <Braces size={13} /> },
    { id: 'source', label: 'Source', icon: <Code2 size={13} /> },
  ]

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        title="View as"
        aria-label="View as"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`rounded p-1 ${
          open || mode !== 'app'
            ? 'bg-muted text-foreground'
            : 'text-muted-foreground/70 hover:bg-muted/60 hover:text-foreground'
        }`}
      >
        <MoreHorizontal size={14} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-7 right-0 z-40 min-w-[9rem] overflow-hidden rounded-md border border-border bg-background p-1 shadow-xl"
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitemradio"
              aria-checked={mode === item.id}
              onClick={() => {
                onSelect(item.id)
                setOpen(false)
              }}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] ${
                mode === item.id
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
            >
              <span className="shrink-0">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
